/**
 * Controlador do loop: busca as listagens dos produtos ativos, calcula o plano
 * (quadros, tamanho, URLs por camada) e mantém o `useTimeStore` atualizado.
 */
import { getWidth } from 'ol/extent';
import { getProductName } from '../config/products';
import { chooseLoopWidth, chooseStillWidth, detectDeviceProfile, type DeviceProfile } from '../goes/budget';
import { loadListing, useCatalogStore, type CatalogEntry } from '../goes/catalog';
import type { Listing, ListingFrame } from '../goes/listing';
import { APPROX_BYTES, type ImageWidth } from '../goes/star';
import { frameCache } from '../goes/frameCache';
import { compressedBudget, sweepDisk, useFrameStorage } from '../goes/frameStorage';
import { isAbortError } from '../lib/fetchRetry';
import { shallowArrayEqual, subscribeSelector } from '../lib/store';
import { NSA_EXTENT } from '../map/projection';
import { useGoesStore, type GoesLayerState } from '../state/goes';
import { useTimeStore, type TimeRange } from '../state/time';
import { dismissToast, showToast } from '../state/toasts';
import { useViewStore } from '../state/view';
import { HOUR_MS } from '../lib/time';
import { useConexaoStore } from '../detalhe/conexao';
import { decidirBaseDeAbertura, useDetalheStore } from '../detalhe/estado';
import { detalheLigado, modoDaBase } from '../detalhe/regras';
import { useBlocosStore } from '../detalhe/saude';
import {
  alignTimes,
  computeWindow,
  customIntersects,
  HISTORY_LIMIT_NOTE,
  historyTimes,
  latestWindowLike,
  MAX_HISTORY_HOURS,
  PERIOD_HOURS,
  resolveIndex,
  selectFrames,
  truncateToLatest,
  type PeriodPreset,
} from './frames';
import { layerUrlAt, usePlanStore, widthFromUrl, type LoopPlan, type PlanLayer } from './plan';
import { linhaDoTempo, trechoDoQuadro, type TrechoSemBase } from './semNoaa';
import { useLoopStore } from './store';

const SECTOR_WIDTH = getWidth(NSA_EXTENT);

/**
 * Relógio da linha do tempo sem a NOAA (semNoaa.ts). Trocado só nos testes:
 * nos unitários por `definirRelogioDoLoop`; no e2e, que usa o STAR de verdade
 * e espera a imagem da base no quadro mais recente, `window.__amaviewNoaaEmDia`
 * (e2e/teste.ts) faz a NOAA nunca parecer parada.
 */
const relogioPadrao = (): number =>
  typeof window !== 'undefined' && (window as { __amaviewNoaaEmDia?: boolean }).__amaviewNoaaEmDia === true
    ? Number.NEGATIVE_INFINITY
    : Date.now();
let relogio: () => number = relogioPadrao;
export function definirRelogioDoLoop(fn: (() => number) | null): void {
  relogio = fn ?? relogioPadrao;
}

/** Aviso do link que pede algo anterior às últimas 48 h. */
const FORA_DAS_48H = (oQue: string) =>
  `${oQue} é anterior às últimas ${MAX_HISTORY_HOURS} h. ${HISTORY_LIMIT_NOTE} Mostrando as imagens mais recentes.`;

/** Largura do setor em pixels CSS na resolução atual do mapa (ou null). */
export function sectorCssPixels(): number | null {
  const r = useViewStore.getState().resolution;
  return r ? SECTOR_WIDTH / r : null;
}

// ---------------------------------------------------------------------------
// Listagens (com nova tentativa automática)
// ---------------------------------------------------------------------------

interface RetryState {
  attempts: number;
  timer: ReturnType<typeof setTimeout> | null;
}
const retries = new Map<string, RetryState>();

function activeProductIds(): string[] {
  return [...new Set(useGoesStore.getState().layers.map((l) => l.productId))];
}

async function fetchListing(productId: string, force: boolean): Promise<boolean> {
  const before = useCatalogStore.getState().entries[productId]?.listing?.times.at(-1) ?? null;
  try {
    const listing = await loadListing(productId, { force });
    const st = retries.get(productId);
    if (st?.timer) clearTimeout(st.timer);
    retries.delete(productId);
    dismissToast(`listing-${productId}`);
    return listing.times.at(-1) !== before;
  } catch (e) {
    if (isAbortError(e)) return false;
    // Sem conexão: nada de novas tentativas nem avisos repetidos; o evento
    // `online` (autoRefresh) busca tudo de novo.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    const st = retries.get(productId) ?? { attempts: 0, timer: null };
    st.attempts++;
    const delay = Math.min(120_000, 15_000 * 2 ** (st.attempts - 1));
    const hasOld = !!useCatalogStore.getState().entries[productId]?.listing;
    // Sem listagem ainda: o aviso principal é o do centro do mapa (ou o do
    // cartão da camada); o toast só aparece quando uma ATUALIZAÇÃO falha.
    if (hasOld) {
      showToast({
        id: `listing-${productId}`,
        kind: 'warning',
        message: `Não deu para atualizar “${getProductName(productId)}”. Nova tentativa em ${Math.round(delay / 1000)} s.`,
        timeoutMs: 8000,
        action: { label: 'Tentar agora', onClick: () => void retryListingNow(productId) },
      });
    }
    if (st.timer) clearTimeout(st.timer);
    st.timer = setTimeout(() => {
      st.timer = null;
      if (activeProductIds().includes(productId)) void fetchListing(productId, true);
    }, delay);
    retries.set(productId, st);
    return false;
  }
}

function retryListingNow(productId: string): Promise<boolean> {
  const st = retries.get(productId);
  if (st?.timer) clearTimeout(st.timer);
  if (st) st.timer = null;
  dismissToast(`listing-${productId}`);
  return fetchListing(productId, true);
}

/** Busca as listagens que faltam para as camadas atuais. */
function ensureListings(): void {
  const entries = useCatalogStore.getState().entries;
  for (const id of activeProductIds()) {
    if (entries[id]?.listing || retries.get(id)?.timer) continue;
    void fetchListing(id, false);
  }
}

/**
 * Recarrega as listagens dos produtos ativos (atualização automática ou
 * "Verificar agora"). Retorna true se apareceu quadro novo em algum produto.
 */
export async function refreshListings(): Promise<boolean> {
  const results = await Promise.all(activeProductIds().map((id) => fetchListing(id, true)));
  return results.some(Boolean);
}

// ---------------------------------------------------------------------------
// Plano do loop
// ---------------------------------------------------------------------------

const sizeIndexCache = new WeakMap<Listing, Map<number, ListingFrame['bytes']>>();
function sizeIndex(listing: Listing): Map<number, ListingFrame['bytes']> {
  let idx = sizeIndexCache.get(listing);
  if (!idx) {
    idx = new Map(listing.frames.map((f) => [f.time, f.bytes]));
    sizeIndexCache.set(listing, idx);
  }
  return idx;
}

function buildPlanLayer(
  layer: GoesLayerState,
  isBase: boolean,
  frames: number[],
  entry: CatalogEntry | undefined,
  width: ImageWidth,
  trechos: readonly TrechoSemBase[] = [],
): PlanLayer {
  const listing = entry?.listing;
  if (!listing) {
    return { uid: layer.uid, productId: layer.productId, visible: layer.visible, pending: true, times: [], sizes: [], urls: [] };
  }
  // Nos trechos sem a base (a NOAA parou de publicá-la, semNoaa.ts) a base fica
  // sem imagem. As outras, em todo quadro, só com a imagem da mesma varredura
  // (`ALIGN_TOLERANCE_MS`): sem ela, ficam escondidas, nunca com a de outro horário.
  const times = isBase ? frames.map((t) => (trechoDoQuadro(trechos, t) ? null : t)) : alignTimes(frames, listing.times);
  const idx = sizeIndex(listing);
  const sizes = times.map((t) => (t === null ? null : (idx.get(t) ?? null)));
  const pl: PlanLayer = { uid: layer.uid, productId: layer.productId, visible: layer.visible, pending: false, times, sizes, urls: [] };
  pl.urls = times.map((_, i) => layerUrlAt(pl, i, width));
  return pl;
}

function estimateBytes(layers: PlanLayer[]): number {
  let total = 0;
  for (const l of layers) {
    if (!l.visible || l.pending) continue;
    l.urls.forEach((url, i) => {
      if (!url) return;
      const w = widthFromUrl(url);
      const b = l.sizes[i]?.[w];
      total += b !== undefined && Number.isFinite(b) ? b : APPROX_BYTES[w];
    });
  }
  return total;
}

function planSignature(plan: LoopPlan): string {
  return [
    plan.frames.join(','),
    plan.width,
    plan.capacity,
    plan.requested,
    plan.semBaseDesde ?? '',
    (plan.trechosSemBase ?? []).map((t) => `${t.desde}-${t.ate}`).join(','),
    ...plan.layers.map((l) => `${l.uid}:${l.productId}:${l.visible}:${l.pending}:${l.urls.join('|')}`),
  ].join(';');
}

let lastSignature = '';
/** Houve mudança adiada pela trava de exportação (aplicar ao destravar). */
let deferredByLock = false;
/** Maior largura automática já usada na sessão. */
let autoCeiling: ImageWidth | null = null;

function recompute(profile: DeviceProfile): void {
  // Enquanto mede o aparelho (disco ou RAM), nenhum plano: o primeiro já sai
  // com o orçamento certo, sem cortar e depois estender o loop.
  if (useFrameStorage.getState().mode === 'pendente') return;
  const { layers } = useGoesStore.getState();
  const loop = useLoopStore.getState();
  if (loop.exportLocked) {
    // Exportando: `frames`/`range` ficam congelados; recalcula ao destravar.
    deferredByLock = true;
    return;
  }
  const entries = useCatalogStore.getState().entries;
  const prevPlan = usePlanStore.getState().plan;
  const base = layers[0];
  const baseListing = base ? entries[base.productId]?.listing : null;

  if (!base || !baseListing) {
    // Linha do tempo indisponível (ex.: trocando o produto da base): mantém
    // os quadros atuais e marca as camadas sem listagem como pendentes.
    if (prevPlan) {
      const nextLayers = layers.map((l, i) => {
        const old = prevPlan.layers.find((p) => p.uid === l.uid);
        if (old && old.productId === l.productId && !old.pending) return { ...old, visible: l.visible };
        // Camadas sobrepostas com listagem já podem se alinhar aos quadros atuais.
        return buildPlanLayer(l, false, prevPlan.frames, i > 0 ? entries[l.productId] : undefined, prevPlan.width, prevPlan.trechosSemBase);
      });
      publish({ ...prevPlan, layers: nextLayers, bytesTotal: estimateBytes(nextLayers) });
    }
    return;
  }

  // A NOAA parou de publicar a base: a linha do tempo segue numa grade de
  // 10 min até agora, sem ela, com as outras camadas (semNoaa.ts).
  const outrasUltimas = layers
    .slice(1)
    .filter((l) => l.visible)
    .map((l) => entries[l.productId]?.listing?.times.at(-1))
    .filter((t): t is number => t !== undefined);
  const { times: linha, semBaseDesde, trechos } = linhaDoTempo(baseListing.times, outrasUltimas, relogio());
  // A linha do tempo oferecida é só a das últimas 48 h (`MAX_HISTORY_HOURS`),
  // embora a NOAA guarde mais: as outras camadas não vão além disso.
  const times = historyTimes(linha);

  // Período personalizado fora das últimas 48 h (ex.: link antigo): mesma
  // duração, terminando no quadro mais recente.
  if (loop.period === 'custom' && loop.custom && !customIntersects(times, loop.custom)) {
    useLoopStore.setState({ custom: latestWindowLike(times, loop.custom) });
    showToast({ id: 'custom-out-of-range', kind: 'warning', message: FORA_DAS_48H('O período do link'), timeoutMs: 9000 });
    return; // a mudança no store agenda um novo cálculo
  }

  const window = computeWindow(times, loop.period, loop.custom);
  if (!window) return;
  const relative = loop.period !== 'custom';
  const allFrames = selectFrames(times, window, loop.step, { relative });

  // Link com um quadro que já saiu da janela relativa ("últimas N h"): troca
  // para um período personalizado de mesma duração em volta dele, para quem
  // abre o link ver exatamente o quadro compartilhado.
  const pendingFrame = loop.pendingFrameTime;
  if (
    relative &&
    pendingFrame !== null &&
    !allFrames.includes(pendingFrame) &&
    pendingFrame >= times[0]! &&
    pendingFrame <= times[times.length - 1]!
  ) {
    const half = (PERIOD_HOURS[loop.period as PeriodPreset] * HOUR_MS) / 2;
    useLoopStore.setState({ period: 'custom', custom: { start: pendingFrame - half, end: pendingFrame + half } });
    return; // a mudança no store agenda um novo cálculo
  }
  if (pendingFrame !== null && pendingFrame < times[0]!) {
    // Quadro do link anterior às últimas 48 h: segue no mais recente, avisando.
    showToast({ id: 'custom-out-of-range', kind: 'warning', message: FORA_DAS_48H('O horário do link'), timeoutMs: 9000 });
  }
  const layerCount = Math.max(1, layers.filter((l) => l.visible).length);
  // Bytes comprimidos do loop inteiro em cada largura (tamanhos reais da listagem).
  const bytesCache = new Map<ImageWidth, number>();
  const bytesFor = (w: ImageWidth): number => {
    let b = bytesCache.get(w);
    if (b === undefined) {
      b = estimateBytes(layers.map((l, i) => buildPlanLayer(l, i === 0, allFrames, entries[l.productId], w, trechos)));
      bytesCache.set(w, b);
    }
    return b;
  };
  // Com o detalhe em blocos, a base segue a automática (qualidade fixa "Alta"
  // ou "Máxima" é o teto dos blocos, regras.ts).
  const mode = modoDaBase(loop.quality, detalheLigado());
  const choice = chooseLoopWidth({
    mode,
    sectorCssPixels: sectorCssPixels(),
    frameCount: allFrames.length,
    // Arquivos no disco: o orçamento é o do disco, não o da RAM (frameStorage.ts).
    profile: { ...profile, budgetBytes: compressedBudget(profile) },
    bytesFor,
    autoFloor: autoCeiling,
  });
  // A automática só sobe durante a sessão (afastar o zoom não baixa tudo de novo).
  if (mode === 'auto') autoCeiling = Math.max(autoCeiling ?? 0, choice.width) as ImageWidth;
  const frames = truncateToLatest(allFrames, choice.capacity);
  const truncated = frames.length < allFrames.length;
  const planLayers = layers.map((l, i) => buildPlanLayer(l, i === 0, frames, entries[l.productId], choice.width, trechos));

  const plan: LoopPlan = {
    frames,
    width: choice.width,
    capacity: choice.capacity,
    requested: allFrames.length,
    truncated,
    layers: planLayers,
    layerCount,
    bytesTotal: estimateBytes(planLayers),
    semBaseDesde,
    trechosSemBase: trechos,
  };

  const range: TimeRange = { start: truncated && frames.length ? frames[0] : window.start, end: window.end };
  const ts = useTimeStore.getState();
  const pendingTime = loop.pendingFrameTime;
  const rangeChanged = !ts.range || ts.range.start !== range.start || ts.range.end !== range.end;
  if (!shallowArrayEqual(ts.frames, frames) || rangeChanged || pendingTime !== null) {
    ts.replaceFrames(frames, range, resolveIndex(ts.frames, ts.index, frames, pendingTime));
  }
  if (pendingTime !== null) useLoopStore.setState({ pendingFrameTime: null });
  // A faxina só quando o plano muda: sem a base, o relógio recalcula a cada minuto.
  if (publish(plan)) scheduleSweep(plan, times[times.length - 1]! - MAX_HISTORY_HOURS * HOUR_MS);
}

/** Espera o plano assentar antes da faxina do disco (trocas seguidas de período/qualidade). */
const SWEEP_DELAY_MS = 10_000;
let sweepTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Faxina do disco depois que o plano assenta: sai o que não está no cache e
 * não vale guardar (`retainOnDisk`) — quadros de mais de 48 h, larguras e
 * produtos que não estão mais em uso, sobras de visitas anteriores.
 */
function scheduleSweep(plan: LoopPlan, since: number): void {
  if (useFrameStorage.getState().mode !== 'disco') return;
  if (sweepTimer) clearTimeout(sweepTimer);
  sweepTimer = setTimeout(() => {
    sweepTimer = null;
    const wanted = new Set<string>();
    for (const l of plan.layers) for (const u of l.urls) if (u) wanted.add(u);
    void sweepDisk(frameCache, {
      wanted,
      width: plan.width,
      productIds: new Set(plan.layers.map((l) => l.productId)),
      since,
      truncated: plan.truncated,
    }).catch(() => {});
  }, SWEEP_DELAY_MS);
}

/** Publica o plano se ele mudou; true se publicou. */
function publish(plan: LoopPlan): boolean {
  const sig = planSignature(plan);
  if (sig === lastSignature) return false;
  lastSignature = sig;
  usePlanStore.setState({ plan });
  return true;
}

function updateStillWidth(profile: DeviceProfile): void {
  const { plan } = usePlanStore.getState();
  const { playing, quality, exportLocked } = useLoopStore.getState();
  const mode = modoDaBase(quality, detalheLigado());
  const w = plan && !playing && !exportLocked ? chooseStillWidth(mode, sectorCssPixels(), plan.width, profile) : null;
  if (w !== usePlanStore.getState().stillWidth) usePlanStore.setState({ stillWidth: w });
}

/**
 * Com o detalhe em blocos ligado, a base (imagem inteira) vai só até `base`
 * (1800, ou 900 de perto — detalhe/motor.ts): a nitidez acima disso vem em
 * blocos da área visível. Vale também para a qualidade fixa "Alta"/"Máxima",
 * cujo número passa a ser o teto dos blocos (detalhe/regras.ts).
 */
export function profileWithTiles(profile: DeviceProfile, tiles: boolean, base: ImageWidth): DeviceProfile {
  if (!tiles) return profile;
  const cap = (w: ImageWidth) => Math.min(w, base) as ImageWidth;
  return { ...profile, maxAutoLoopWidth: cap(profile.maxAutoLoopWidth), maxStillWidth: cap(profile.maxStillWidth) };
}

/** Liga o controlador. Retorna a função de desligar. */
export function startLoopController(): () => void {
  const deviceProfile = detectDeviceProfile();
  const profile = () => profileWithTiles(deviceProfile, detalheLigado(), useDetalheStore.getState().base);
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      // Abertura: a base certa (900 de perto) já no primeiro plano, não 1800 e depois 900.
      if (!usePlanStore.getState().firstFrameShown) {
        decidirBaseDeAbertura(useGoesStore.getState().layers.filter((l) => l.visible).map((l) => l.productId));
      }
      recompute(profile());
      updateStillWidth(profile());
    });
  };
  let zoomTimer: ReturnType<typeof setTimeout> | null = null;
  // Sem a base, a grade anda com o relógio, e não com as listagens: confere a
  // cada minuto se já cabe mais um quadro (o plano só muda se couber).
  const relogioSemBase = setInterval(() => {
    if (usePlanStore.getState().plan?.semBaseDesde != null) schedule();
  }, 60_000);

  const unsubs = [
    useGoesStore.subscribe(() => {
      ensureListings();
      schedule();
    }),
    useCatalogStore.subscribe(schedule),
    subscribeSelector(
      useLoopStore,
      (s) => [s.period, s.custom?.start, s.custom?.end, s.step, s.quality, s.pendingFrameTime],
      schedule,
      shallowArrayEqual,
    ),
    subscribeSelector(useLoopStore, (s) => s.playing, () => updateStillWidth(profile())),
    // Medição do aparelho concluída, ou o disco encheu: recalcula com o novo orçamento.
    subscribeSelector(useFrameStorage, (s) => `${s.mode}|${s.diskBudget}`, schedule),
    // Servidor de blocos entrou ou saiu do ar, a conexão mudou ou a base de
    // perto trocou: a base muda de teto.
    subscribeSelector(useBlocosStore, (s) => s.estado !== 'fora', schedule),
    subscribeSelector(useConexaoStore, (s) => `${s.motivo}|${s.forcar}`, schedule),
    subscribeSelector(useDetalheStore, (s) => s.base, () => {
      schedule();
      updateStillWidth(profile());
    }),
    subscribeSelector(
      useLoopStore,
      (s) => s.exportLocked,
      (locked) => {
        updateStillWidth(profile());
        if (!locked && deferredByLock) {
          deferredByLock = false;
          schedule();
        }
      },
    ),
    subscribeSelector(
      useViewStore,
      (s) => s.resolution,
      () => {
        updateStillWidth(profile());
        if (modoDaBase(useLoopStore.getState().quality, detalheLigado()) !== 'auto') return;
        if (zoomTimer) clearTimeout(zoomTimer);
        zoomTimer = setTimeout(schedule, 600);
      },
    ),
  ];

  ensureListings();
  schedule();

  return () => {
    unsubs.forEach((u) => u());
    clearInterval(relogioSemBase);
    if (zoomTimer) clearTimeout(zoomTimer);
    for (const st of retries.values()) if (st.timer) clearTimeout(st.timer);
    retries.clear();
    lastSignature = '';
    deferredByLock = false;
    autoCeiling = null;
    if (sweepTimer) clearTimeout(sweepTimer);
    sweepTimer = null;
  };
}
